import { ReactNode } from 'react';
import { ProgressBarInfo } from '@apply/components';

/** One step of a WizardModal: everything the wizard needs to list it, label it, and render it. */
export interface WizardStep {
    /**
     * Identifies the step (currentStep / onStepChange use it) and orders it. The sidebar bubble shows the step's
     * position among the steps given, so numbers can skip (e.g. when a step is left out) without a gap showing.
     */
    number: number;
    /** Short label shown in the sidebar step list. */
    label: string;
    /** Heading shown at the top of the content area while this step is active. */
    header: string;
    /** Optional guidance shown under the header. */
    helpText?: string;
    /** Content rendered in the content area while this step is active, e.g. <PropertyStep loanId={loanId} />. */
    content: ReactNode;
    /**
     * Optional completion info for this step, computed from the wizard's data (not the step's content) so it is
     * known for every step, open or not. Shown as a progress bar under the current step (hidden on mobile, and when
     * the total is 0).
     */
    progressBarInfo?: ProgressBarInfo;
    /**
     * Optional: the step can't be opened yet (e.g. it depends on an earlier step's answer). It's greyed out in the
     * step list and can't be clicked, and Continue/Back won't move onto it.
     */
    disabled?: boolean;
}

/**
 * Which steps can be clicked in the step list:
 * - 'none': no steps are clickable
 * - 'previous': only steps before the current one (jump back)
 * - 'all': any step other than the current one (jump back or forward)
 */
export type WizardStepNavigation = 'none' | 'previous' | 'all';

/** Which steps in the step list show a red warning bubble while their progressBarInfo isn't complete. */
export enum IncompleteStepAlertDetection {
    /** No steps (the default). */
    None = 'none',
    /** Only steps before the current one. */
    Previous = 'previous',
    /** Every step except the current one. */
    All = 'all',
}
